import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Star, MapPin, ShieldCheck, BadgeCheck, Video, Home,
  Globe, Award, Calendar, Sparkles, Loader2, Quote, ArrowRight,
  HeartPulse, CreditCard,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { TrustBadge } from '../components/brand/TrustBadge';
import { SpecialtyBadgeRow } from '../components/brand/SpecialtyBadge';
import { FOCUS_TRACK_BY_ID } from '../lib/clinicianTracks';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import { NegotiateModal } from '../components/brand/NegotiateModal';
import { ShareProfile } from '../components/brand/ShareProfile';
import { Seo } from '../components/seo/Seo';
import { clinicianProfileSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { absoluteUrl } from '../lib/siteConfig';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  in_home_care: 'In-home care', telehealth_visit: 'Telehealth',
  wellness_visit: 'Wellness', companion_care: 'Companion care',
  post_discharge: 'Post-discharge', chronic_care: 'Chronic care',
  hospice_palliative: 'Hospice', mobile_urgent_care: 'Mobile urgent',
  behavioral_health: 'Behavioral health', skilled_nursing: 'Skilled nursing',
};

export default function ClinicianProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openNegotiate, setOpenNegotiate] = useState(false);
  const [payerMap, setPayerMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/public/clinicians/${id}`);
        setData(d);
        // Count one profile view per visitor session (skip the clinician's own profile).
        const viewKey = `cl_viewed_${d.id}`;
        if (d.id && user?.id !== d.id && !sessionStorage.getItem(viewKey)) {
          sessionStorage.setItem(viewKey, '1');
          axios.post(`${API}/public/clinicians/${d.id}/view`).catch(() => {});
        }
      } catch (e) {
        toast.error('Could not load clinician profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    axios.get(`${API}/insurance/payers`)
      .then((r) => {
        const m = {};
        (r.data.payers || []).forEach((p) => { m[p.id] = p.name; });
        (r.data.plan_types || []).forEach((p) => { m[p.id] = p.name; });
        setPayerMap(m);
      })
      .catch(() => {});
  }, []);

  const onBook = () => {
    if (!user) { navigate('/auth'); return; }
    if (user.role !== 'patient') { toast.info('Sign in as a family/patient to book.'); return; }
    setOpenNegotiate(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cn-primary animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-cn-bg">
        <PublicNavbar />
        <div className="cn-container py-24 text-center">
          <p className="text-cn-text-muted">Clinician not found.</p>
          <Link to="/patient/find-clinicians" className="cn-btn cn-btn-ghost mt-4 inline-flex">Find another clinician</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const statusBadge = {
    available: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    busy: { bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-500' },
    offline: { bg: 'bg-cn-bone-200', text: 'text-cn-text-muted', dot: 'bg-cn-text-muted' },
  }[data.availability_status] || { bg: 'bg-cn-bone-200', text: 'text-cn-text-muted', dot: 'bg-cn-secondary' };

  const profilePath = `/clinicians/${data.slug || id}`;
  const shareUrl = absoluteUrl(profilePath);
  const ogImage = data.photo_url
    ? (data.photo_url.startsWith('http') ? data.photo_url : `${process.env.REACT_APP_BACKEND_URL}${data.photo_url}`)
    : undefined;
  const seoTitle = `${data.name}${data.license_type ? ', ' + data.license_type : ''}${data.city ? ' — ' + data.city + ', ' + data.state : ''} | CaraLoom`;
  const seoDesc = (data.headline || data.bio || `Book ${data.name}, a verified clinician on CaraLoom — verified-settlement protected, HIPAA-ready in-home and telehealth care.`).slice(0, 158);

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="clinician-profile-page">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={profilePath}
        image={ogImage}
        schema={[
          clinicianProfileSchema(data, profilePath, ogImage),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Marketplace', path: '/marketplace' },
            { name: data.name, path: profilePath },
          ]),
        ]}
      />
      <PublicNavbar />

      <div className="cn-container py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-primary" data-testid="profile-back-button">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      {/* Hero */}
      <section className="cn-container">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="cn-card overflow-hidden p-0">
              <div className="aspect-[16/10] bg-cn-bone-200 relative">
                {data.photo_url ? (
                  <img src={data.photo_url} alt={data.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cn-sage-300 to-cn-sage-500 text-white text-7xl font-display font-bold">
                    {(data.name || 'C').split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                )}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {data.license_verified && <TrustBadge variant="license" label="License verified" />}
                  {data.npi_verified && <TrustBadge variant="npi-verified" />}
                  {['passed', 'cleared'].includes(data.background_check_status) && <TrustBadge variant="background-checked" />}
                  <TrustBadge variant="encrypted" label="HIPAA-ready" />
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="cn-display text-3xl md:text-4xl font-bold">{data.name}</h1>
                    <span className={`inline-flex items-center gap-1.5 ${statusBadge.bg} ${statusBadge.text} px-2.5 py-1 rounded-full text-xs font-semibold`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot} animate-pulse`} />
                      {data.availability_status === 'available' ? 'Available now' : data.availability_status}
                    </span>
                  </div>
                  <p className="mt-1 text-cn-text-muted">{data.headline}</p>
                  {data.badges?.length > 0 && (
                    <SpecialtyBadgeRow badges={data.badges} className="mt-2.5" />
                  )}
                </div>
                <div className="flex flex-col items-end gap-2.5">
                  <div className="flex items-center gap-1.5">
                    {data.review_count > 0 && data.rating != null ? (
                      <>
                        <Star className="w-4 h-4 fill-cn-terracotta-400 text-cn-terracotta-400" />
                        <span className="font-semibold">{data.rating}</span>
                        <span className="text-cn-text-muted text-sm">({data.review_count} reviews)</span>
                      </>
                    ) : (
                      <span className="text-cn-text-muted text-sm">No reviews yet</span>
                    )}
                  </div>
                  <ShareProfile
                    url={shareUrl}
                    clinicianId={data.id}
                    title={`${data.name} — verified clinician on CaraLoom`}
                    testId="profile-share"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 text-sm text-cn-text-muted">
                {data.city && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {data.city}, {data.state}</span>}
                {data.years_of_experience != null && <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4" /> {data.years_of_experience} yrs experience</span>}
                {data.languages?.length > 0 && <span className="inline-flex items-center gap-1.5"><Globe className="w-4 h-4" /> {data.languages.join(', ')}</span>}
                <span className="inline-flex items-center gap-1.5">
                  {data.available_for_telehealth && <Video className="w-4 h-4" />}
                  {data.available_for_in_person && <Home className="w-4 h-4" />}
                  {[data.available_for_telehealth && 'Telehealth', data.available_for_in_person && 'In-home'].filter(Boolean).join(' · ')}
                </span>
              </div>

              {data.bio && (
                <p className="mt-6 text-cn-text leading-relaxed whitespace-pre-line">{data.bio}</p>
              )}

              {data.specialties?.length > 0 && (
                <div className="mt-6">
                  <div className="cn-eyebrow mb-2">Specialties</div>
                  <div className="flex flex-wrap gap-2">
                    {data.specialties.map((s) => (
                      <span key={s} className="inline-flex items-center gap-1.5 bg-cn-sage-100 text-cn-primary px-3 py-1 rounded-full text-xs font-semibold">
                        <HeartPulse className="w-3 h-3" /> {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {data.clinical_focus_areas?.length > 0 && (
                <div className="mt-6" data-testid="profile-clinical-focus">
                  <div className="cn-eyebrow mb-2">Clinical focus</div>
                  <div className="flex flex-wrap gap-2">
                    {data.clinical_focus_areas.map((fid) => {
                      const t = FOCUS_TRACK_BY_ID[fid];
                      if (!t) return null;
                      return (
                        <span key={fid} className="inline-flex items-center gap-1.5 bg-cn-primary/8 text-cn-primary px-3 py-1 rounded-full text-xs font-semibold">
                          <t.Icon className="w-3 h-3" /> {t.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {data.certifications?.length > 0 && (
                <div className="mt-6">
                  <div className="cn-eyebrow mb-2">Credentials</div>
                  <div className="flex flex-wrap gap-2">
                    {data.certifications.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1.5 bg-white dark:bg-cn-ink-800 border border-cn-border text-cn-text px-3 py-1 rounded-full text-xs font-semibold">
                        <BadgeCheck className="w-3 h-3 text-cn-primary" /> {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {data.accepts_insurance && data.accepted_payers?.length > 0 && (
                <div className="mt-6" data-testid="profile-insurance-section">
                  <div className="cn-eyebrow mb-2 flex items-center gap-2">
                    Insurance accepted
                    {data.insurance_verified && (
                      <span className="inline-flex items-center gap-1 text-cn-success normal-case tracking-normal font-semibold">
                        <BadgeCheck className="w-3.5 h-3.5" /> verified
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.accepted_payers.map((pid) => (
                      <span key={pid} className="inline-flex items-center gap-1.5 bg-cn-primary/8 text-cn-primary px-3 py-1 rounded-full text-xs font-semibold">
                        <CreditCard className="w-3 h-3" /> {payerMap[pid] || pid}
                      </span>
                    ))}
                  </div>
                  {data.accepted_plan_types?.length > 0 && (
                    <p className="mt-2 text-xs text-cn-text-muted">
                      Plan types: {data.accepted_plan_types.map((t) => payerMap[t] || t).join(', ')}
                    </p>
                  )}
                  {data.insurance_notes && (
                    <p className="mt-2 text-sm text-cn-text leading-relaxed italic border-l-2 border-cn-primary/30 pl-3">{data.insurance_notes}</p>
                  )}
                  <p className="mt-2 text-[11px] text-cn-text-muted">
                    Self-attested by the clinician. Please confirm coverage and copays with your plan before booking.
                  </p>
                </div>
              )}

              {data.packages?.length > 0 && (
                <div className="mt-10">
                  <div className="cn-eyebrow mb-3">Service packages</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {data.packages.map((p) => (
                      <div key={p.id} className="cn-card p-5" data-testid={`package-${p.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-display font-semibold">{p.name}</h4>
                            <p className="text-xs text-cn-text-muted mt-0.5">{SERVICE_LABELS[p.service_type] || p.service_type} · {p.duration_hours}h{p.visits_included > 1 ? ` · ${p.visits_included} visits` : ''}</p>
                          </div>
                          <div className="text-right">
                            <div className="font-display font-bold text-lg">${p.price}</div>
                          </div>
                        </div>
                        {p.description && <p className="mt-3 text-sm text-cn-text-muted leading-relaxed">{p.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.reviews?.length > 0 && (
                <div className="mt-10">
                  <div className="cn-eyebrow mb-3">Recent reviews</div>
                  <div className="space-y-4">
                    {data.reviews.slice(0, 6).map((r) => (
                      <figure key={r.id} className="cn-card p-5">
                        <div className="flex items-center gap-1 mb-2">
                          {Array.from({ length: r.rating }).map((_, i) => (
                            <Star key={i} className="w-3.5 h-3.5 fill-cn-terracotta-400 text-cn-terracotta-400" />
                          ))}
                        </div>
                        <Quote className="w-4 h-4 text-cn-terracotta-400 mb-1" />
                        <blockquote className="text-sm text-cn-text leading-relaxed">{r.comment}</blockquote>
                        <figcaption className="mt-3 text-xs text-cn-text-muted">— {r.patient_name}</figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking sidebar */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24 space-y-4">
              <div className="cn-card p-6">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-display text-3xl font-bold">${data.hourly_rate}</div>
                    <div className="text-xs text-cn-text-muted uppercase tracking-wider mt-0.5">starting rate / hr</div>
                  </div>
                  <span className="text-xs text-cn-text-muted">Negotiable</span>
                </div>
                <button
                  onClick={onBook}
                  className="cn-btn cn-btn-primary w-full mt-5"
                  data-testid="profile-book-button"
                >
                  <Sparkles className="w-4 h-4" /> Send offer & book
                </button>
                <button
                  onClick={() => navigate('/auth')}
                  className="cn-btn cn-btn-ghost w-full mt-2"
                  data-testid="profile-message-button"
                >
                  Message {data.name?.split(' ')[0]}
                </button>
                <div className="mt-6 space-y-3 text-sm text-cn-text">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-cn-primary" /> Verified-settlement protected booking
                  </div>
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-cn-primary" /> Verified credentials
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-cn-primary" /> Most visits booked within 24h
                  </div>
                  {data.accepts_insurance && (
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-cn-primary" /> Accepts insurance
                    </div>
                  )}
                </div>
              </div>

              <MarketplaceDisclaimer variant="inline" />
            </div>
          </aside>
        </div>

        <div className="my-16">
          <MarketplaceDisclaimer variant="banner" />
        </div>
      </section>

      <Footer />

      <NegotiateModal
        open={openNegotiate}
        onOpenChange={setOpenNegotiate}
        clinician={{ id: data.id, first_name: data.name?.split(' ')[0], name: data.name }}
        token={token}
        onCreated={() => navigate('/patient/offers')}
      />
    </div>
  );
}
